import React, { useState, useEffect } from "react";
import {
  DollarSign,
  TrendingUp,
  Trophy,
  Users,
  ChevronUp,
  ChevronDown,
  Activity,
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend,
} from "recharts";
import { ref, get } from "firebase/database";
import { database } from "../Config/firebase";

export default function AppAnality() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    pronosticosGanados: 0,
    totalPronosticos: 0,
    beneficioTotal: 0,
    rachaActual: 0,
    roi: 0,
    mejorRacha: 0,
    mayorBeneficio: 0,
    seguidores: "0",
    rendimientoSemanal: [],
    vipWinRate: 0,
    gratisWinRate: 0,
    comparativaVipGratis: [],
  });
  const [animatedStats, setAnimatedStats] = useState({
    pronosticosGanados: 0,
    beneficioTotal: 0,
    rachaActual: 0,
    roi: 0,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Obtener datos VIP
        const vipRef = ref(database, "abetvip");
        const vipSnapshot = await get(vipRef);
        const vipData = vipSnapshot.val();

        // Obtener datos Gratis
        const gratisRef = ref(database, "abet");
        const gratisSnapshot = await get(gratisRef);
        const gratisData = gratisSnapshot.val();

        // Procesar datos
        const processedData = processPronosticos(vipData, gratisData);
        setStats(processedData);
        setLoading(false);
      } catch (error) {
        console.error("Error al cargar datos de Firebase:", error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (!loading) {
      animateStats();
    }
  }, [loading]);

  const processPronosticos = (vipData, gratisData) => {
    // Inicializar contadores
    let vipGanados = 0;
    let vipTotal = 0;
    let vipPerdidos = 0;
    let gratisGanados = 0;
    let gratisTotal = 0;
    let gratisPerdidos = 0;
    let beneficioTotal = 0;
    let rachaActual = 0;
    let mejorRacha = 0;
    let rachaTemp = 0;
    let mayorBeneficio = 0;

    // Para almacenar rendimiento por día
    const diasSemana = ["Lun", "Mar", "Mie", "Jue", "Vie", "Sab", "Dom"];
    const rendimientoPorDia = {};
    diasSemana.forEach((dia) => {
      rendimientoPorDia[dia] = { ganados: 0, perdidos: 0, total: 0 };
    });

    // Procesar pronósticos VIP
    if (vipData) {
      // Convertir a array si es un objeto
      const vipEntries = Array.isArray(vipData)
        ? vipData
        : Object.entries(vipData).map(([key, value]) => ({
            id: key,
            ...value,
          }));

      vipEntries.forEach((pronostico) => {
        if (pronostico.state) {
          vipTotal++;

          // Registrar día de la semana si hay fecha
          if (pronostico.date) {
            // Convertir formato DD/MM/YYYY a objeto Date
            const [day, month, year] = pronostico.date.split("/").map(Number);
            const fecha = new Date(year, month - 1, day);

            if (!isNaN(fecha.getTime())) {
              // Verificar que la fecha sea válida
              const diaSemana = fecha.getDay(); // 0 = domingo, 1 = lunes, etc.
              const indexDia = diaSemana === 0 ? 6 : diaSemana - 1; // Ajustar para que lunes sea 0
              const nombreDia = diasSemana[indexDia];

              rendimientoPorDia[nombreDia].total++;

              if (pronostico.state === "ganada") {
                rendimientoPorDia[nombreDia].ganados++;
              } else if (pronostico.state === "perdida") {
                rendimientoPorDia[nombreDia].perdidos++;
              }
            }
          }

          if (pronostico.state === "ganada") {
            vipGanados++;
            // Calcular beneficio usando la cuota del pronóstico
            const cuota = pronostico.odds || 1.8;
            beneficioTotal += (cuota - 1) * 10000; // Asumiendo stake de 10000

            // Actualizar racha
            rachaTemp++;
            if (rachaTemp > mejorRacha) {
              mejorRacha = rachaTemp;
            }
          } else if (pronostico.state === "perdida") {
            vipPerdidos++;
            beneficioTotal -= 10000; // Perdemos el stake
            rachaTemp = 0;
          }
        }
      });
    }

    // Procesar pronósticos gratis
    if (gratisData) {
      // Convertir a array si es un objeto
      const gratisEntries = Array.isArray(gratisData)
        ? gratisData
        : Object.entries(gratisData).map(([key, value]) => ({
            id: key,
            ...value,
          }));

      gratisEntries.forEach((pronostico) => {
        if (pronostico.state) {
          gratisTotal++;

          if (pronostico.state === "ganada") {
            gratisGanados++;
          } else if (pronostico.state === "perdida") {
            gratisPerdidos++;
          }
        }
      });
    }

    // Racha actual es la racha temporal al final del procesamiento
    rachaActual = rachaTemp;

    // Calcular mayor beneficio (ejemplo simplificado)
    mayorBeneficio = Math.max(beneficioTotal, 500000);

    // Calcular ROI
    const totalInvertido = vipTotal * 10000;
    const roi =
      totalInvertido > 0 ? (beneficioTotal / totalInvertido) * 100 : 0;

    // Generar datos de rendimiento semanal
    const rendimientoSemanal = [];
    diasSemana.forEach((dia) => {
      const totalDia = rendimientoPorDia[dia].total;
      const ganadosDia = rendimientoPorDia[dia].ganados;

      let valorDia = 50; // Valor base
      if (totalDia > 0) {
        const winRateDia = (ganadosDia / totalDia) * 100;
        valorDia = Math.max(30, Math.min(100, winRateDia));
      }

      rendimientoSemanal.push({
        name: dia,
        value: valorDia,
      });
    });

    // Calcular porcentajes de victorias
    const vipWinRate = vipTotal > 0 ? (vipGanados / vipTotal) * 100 : 0;
    const gratisWinRate =
      gratisTotal > 0 ? (gratisGanados / gratisTotal) * 100 : 0;

    // Datos para gráfico comparativo
    const comparativaVipGratis = [
      { name: "VIP", ganados: vipGanados, perdidos: vipPerdidos },
      { name: "Gratis", ganados: gratisGanados, perdidos: gratisPerdidos },
    ];

    return {
      pronosticosGanados: vipGanados,
      totalPronosticos: vipTotal,
      beneficioTotal: beneficioTotal,
      rachaActual: rachaActual,
      roi: parseFloat(roi.toFixed(1)),
      mejorRacha: mejorRacha,
      mayorBeneficio: mayorBeneficio,
      seguidores: "8.2k", // Dato de ejemplo, reemplazar con dato real si está disponible
      rendimientoSemanal: rendimientoSemanal,
      vipWinRate: parseFloat(vipWinRate.toFixed(1)),
      gratisWinRate: parseFloat(gratisWinRate.toFixed(1)),
      comparativaVipGratis,
    };
  };

  const animateStats = () => {
    const targetStats = {
      pronosticosGanados: stats.pronosticosGanados,
      beneficioTotal: stats.beneficioTotal,
      rachaActual: stats.rachaActual,
      roi: stats.roi,
    };

    const animationDuration = 2000;
    const steps = 60;
    const interval = animationDuration / steps;

    let currentStep = 0;

    const animation = setInterval(() => {
      if (currentStep >= steps) {
        clearInterval(animation);
        setAnimatedStats(targetStats);
        return;
      }

      const progress = (currentStep + 1) / steps;
      setAnimatedStats({
        pronosticosGanados: Math.round(
          targetStats.pronosticosGanados * progress
        ),
        beneficioTotal: Math.round(targetStats.beneficioTotal * progress),
        rachaActual: Math.round(targetStats.rachaActual * progress),
        roi: Number((targetStats.roi * progress).toFixed(1)),
      });

      currentStep++;
    }, interval);

    return () => clearInterval(animation);
  };

  const StatCard = ({
    title,
    value,
    icon: Icon,
    trend,
    chart = false,
    color = "green",
    chartData,
  }) => (
    <div className="bg-gray-800/50 backdrop-blur-lg rounded-xl p-4 md:p-6 transform transition-all duration-300 hover:scale-105 hover:shadow-xl border border-gray-700/50 w-full">
      <div className="flex items-center justify-between mb-2 md:mb-4">
        <div className="flex items-center space-x-2 md:space-x-3">
          <div className={`p-2 md:p-3 rounded-lg bg-${color}-500/10`}>
            <Icon className={`w-4 h-4 md:w-6 md:h-6 text-${color}-500`} />
          </div>
          <h3 className="text-gray-400 font-medium text-sm md:text-base">
            {title}
          </h3>
        </div>
        <div
          className={`flex items-center text-${color}-500 text-xs md:text-sm`}
        >
          {trend === "up" ? (
            <ChevronUp className="w-3 h-3 md:w-4 md:h-4" />
          ) : (
            <ChevronDown className="w-3 h-3 md:w-4 md:h-4" />
          )}
          <span>{trend === "up" ? "+2.5%" : "-2.5%"}</span>
        </div>
      </div>

      <div className="space-y-1 md:space-y-2">
        <div className="flex items-end justify-between">
          <h2 className="text-xl md:text-3xl font-bold text-white">{value}</h2>
          {chart && (
            <div className="h-12 md:h-16 w-24 md:w-32">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData || stats.rendimientoSemanal}>
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke={`var(--${color}-500)`}
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderDashboard = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center h-64">
          <div className="text-center text-white">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
            <p>Cargando datos...</p>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6 md:space-y-8">
        <div className="flex items-center justify-between">
          <h1 className="text-xl md:text-2xl font-bold text-white">
            Rendimiento
          </h1>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          <StatCard
            title="Pronósticos Ganados"
            value={`${animatedStats.pronosticosGanados}/${stats.totalPronosticos}`}
            icon={TrendingUp}
            trend={stats.vipWinRate > 50 ? "up" : "down"}
            chart={true}
            color="green"
          />
          <StatCard
            title="Beneficio Total"
            value={`$${animatedStats.beneficioTotal.toLocaleString()}`}
            icon={DollarSign}
            trend={stats.beneficioTotal > 0 ? "up" : "down"}
            chart={true}
            color="blue"
          />
          <StatCard
            title="Racha Actual"
            value={animatedStats.rachaActual}
            icon={Trophy}
            trend="up"
            chart={true}
            color="yellow"
          />
          <StatCard
            title="ROI"
            value={`${animatedStats.roi}%`}
            icon={Activity}
            trend={stats.roi > 0 ? "up" : "down"}
            chart={true}
            color="purple"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
          <div className="bg-gray-800/50 backdrop-blur-lg rounded-xl p-4 md:p-6 border border-gray-700/50">
            <h3 className="text-lg md:text-xl font-bold text-white mb-2 md:mb-4">
              Rendimiento Semanal
            </h3>
            <div className="h-48 md:h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={stats.rendimientoSemanal}>
                  <XAxis dataKey="name" stroke="#fff" />
                  <YAxis stroke="#fff" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#1F2937",
                      border: "none",
                    }}
                    labelStyle={{ color: "#fff" }}
                  />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#10B981"
                    strokeWidth={2}
                    dot={{ stroke: "#10B981", strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 8 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-gray-800/50 backdrop-blur-lg rounded-xl p-4 md:p-6 border border-gray-700/50">
            <h3 className="text-lg md:text-xl font-bold text-white mb-2 md:mb-4">
              Comparativa VIP vs Gratis
            </h3>
            <div className="h-48 md:h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.comparativaVipGratis}>
                  <XAxis dataKey="name" stroke="#fff" />
                  <YAxis stroke="#fff" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#1F2937",
                      border: "none",
                    }}
                    labelStyle={{ color: "#fff" }}
                  />
                  <Legend />
                  <Bar dataKey="ganados" fill="#10B981" name="Ganados" />
                  <Bar dataKey="perdidos" fill="#EF4444" name="Perdidos" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <div className="bg-gray-800/50 backdrop-blur-lg rounded-xl p-4 md:p-6 border border-gray-700/50">
          <h3 className="text-lg md:text-xl font-bold text-white mb-2 md:mb-4">
            Estadísticas Destacadas
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
            {[
              {
                label: "Mejor Racha",
                value: `${stats.mejorRacha} Aciertos`,
                icon: Trophy,
                color: "yellow",
              },
              {
                label: "Mayor Beneficio",
                value: `$${stats.mayorBeneficio.toLocaleString()}`,
                icon: DollarSign,
                color: "green",
              },
              {
                label: "Seguidores",
                value: stats.seguidores,
                icon: Users,
                color: "blue",
              },
              {
                label: "Win Rate VIP",
                value: `${stats.vipWinRate}%`,
                icon: TrendingUp,
                color: "purple",
              },
              {
                label: "Win Rate Gratis",
                value: `${stats.gratisWinRate}%`,
                icon: TrendingUp,
                color: "green",
              },
              {
                label: "Efectividad",
                value: `${Math.max(
                  stats.vipWinRate,
                  stats.gratisWinRate
                ).toFixed(1)}%`,
                icon: Activity,
                color: "blue",
              },
            ].map((stat, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 md:p-4 bg-gray-700/30 rounded-lg"
              >
                <div className="flex items-center space-x-2 md:space-x-3">
                  <div
                    className={`p-1 md:p-2 rounded-lg bg-${stat.color}-500/10`}
                  >
                    <stat.icon
                      className={`w-4 h-4 md:w-5 md:h-5 text-${stat.color}-500`}
                    />
                  </div>
                  <span className="text-gray-300 text-sm md:text-base">
                    {stat.label}
                  </span>
                </div>
                <span className="text-white font-semibold text-sm md:text-base">
                  {stat.value}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      <main className="flex-1 overflow-y-auto p-3 md:p-4 lg:p-8">
        <div className="mx-auto max-w-7xl">{renderDashboard()}</div>
      </main>
    </div>
  );
}
